// The plan's box on the Architecture tab (FR-ARCH-06).
//
// The plan is written in a form of its own, apart from Redraft, Try again,
// Rebind, Re-pin to head and Discard, which each post on their own; and
// leaving the page drops what was typed. So, while the box holds text that
// isn't the version it started from (its text's SHA-256 against the
// version's, which the form carries as `based_on`; none before version 1,
// when any text is new but the starting skeleton the box is served with,
// its `data-start-text`):
//
//   - leaving the page makes the browser ask first, as the spec editor's
//     unsaved text does (FR-EDIT-27); Save takes the text along, so it
//     doesn't ask;
//   - a button that drops the text (`data-unsaved-confirm`) asks its
//     question first, in the browser's own dialog, and does nothing unless
//     the answer is yes (then the browser doesn't ask again as it leaves);
//   - the text is kept in this browser's storage until it is saved, a drop
//     is confirmed, or it is the version's again. Back on the tab over the
//     same version it goes back in the box; over a newer one it is shown
//     to copy, never put over that version (a save never overwrites a
//     newer version). Either way the box's fold, **Edit the plan by hand**
//     (FR-ARCH-11), is opened, so what was kept is in sight.
//   - **Start from version 3** serves the box with that version's text
//     (`data-started-from`), which is unsaved typing from the start. Kept
//     text typed after the same start goes back in the box (a reload, or
//     Back to that address); any other was typed before it, and is shown
//     to copy rather than written over.
//
// Without the bundle the page works as it always did.
//
// A link into a fold opens it first (FR-ARCH-18).
//
// It also says what a write on the architecture did (FR-ARCH-16): the
// page a write comes back to carries its outcome as a status notice, or the
// drafting notice a Draft, Redraft or Try again led to, marked
// `data-arch-done`; a screen reader doesn't hear a region that is there as
// the page loads, so its sentence is said once the page has been read out
// (FR-UI-14), as the editor says what a save did.

import { announceAfterLoad, spokenText } from "./announce";
import { sha256Hex } from "./digest";
import { AFTER_REFRESH } from "./live";
import { stayed } from "./motion";

/** The plan's box, in the form Save posts. */
const BOX = "arch-text";

/** The line said when the kept text goes back in the box. */
const RESTORED = "arch-plan-restored";

/** Where text kept from an earlier version is shown to copy. */
const KEPT_PLACE = "arch-plan-kept-box";
const KEPT_FIELD = "arch-plan-kept";

/** Open the fold `el` is in, if it is in one (FR-ARCH-11). */
function openFold(el: Element): void {
  const fold = el.closest("details");
  if (fold instanceof HTMLDetailsElement) fold.open = true;
}

/** What the browser keeps for the box: its text and the version it
 * started from (`based_on`, empty before version 1), and the earlier
 * version **Start from** put in it, if it did (FR-ARCH-06). */
export interface KeptPlan {
  text: string;
  basedOn: string;
  from?: string;
}

/**
 * Whether `text` is the version the box started from: the one whose
 * SHA-256 is `basedOn`, or, before version 1, nothing at all or just the
 * skeleton the box was served with (`start`), which isn't saved until Save
 * (FR-ARCH-06).
 */
export function isVersion(text: string, basedOn: string, start = ""): boolean {
  if (basedOn !== "") return sha256Hex(text) === basedOn;
  const typed = text.trim();
  return typed === "" || typed === start.trim();
}

function read(key: string): KeptPlan | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const kept = JSON.parse(raw) as Partial<KeptPlan> | null;
    if (typeof kept?.text !== "string" || typeof kept.basedOn !== "string") {
      return null;
    }
    const from = typeof kept.from === "string" ? kept.from : "";
    return from === ""
      ? { text: kept.text, basedOn: kept.basedOn }
      : { text: kept.text, basedOn: kept.basedOn, from };
  } catch {
    return null;
  }
}

function write(key: string, kept: KeptPlan): void {
  try {
    localStorage.setItem(key, JSON.stringify(kept));
  } catch {
    // No storage here: leaving still asks first.
  }
}

function forget(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing was kept.
  }
}

/** The question a submit asks before it drops the box's text, if any. */
function questionOf(event: SubmitEvent, form: HTMLFormElement): string {
  const from = event.submitter;
  const own = from instanceof HTMLElement ? from.dataset.unsavedConfirm : "";
  if (own) return own;
  return (
    form.querySelector<HTMLElement>("[data-unsaved-confirm]")?.dataset
      .unsavedConfirm ?? ""
  );
}

/** A live refresh can insert or replace the editor after drafting. Only
 * the current box owns listeners and a leaving guard. */
let mounted: { box: HTMLTextAreaElement; stop: () => void } | null = null;

export function installPlanHooks(): void {
  const box = document.getElementById(BOX);
  if (mounted?.box === box) return;
  mounted?.stop();
  mounted = null;
  if (!(box instanceof HTMLTextAreaElement) || !box.form) return;
  const listeners = new AbortController();
  const { signal } = listeners;
  mounted = { box, stop: () => listeners.abort() };
  const form = box.form;
  // Keyed by where Save posts, which a page a refused save brought back
  // shares with the tab.
  const key = `specter-plan:${new URL(form.action, window.location.href).pathname}`;
  const earlierKey = `${key}:earlier`;
  const basedOn =
    form.querySelector<HTMLInputElement>('input[name="based_on"]')?.value ?? "";
  // Before version 1 the box is served with a skeleton, which is no typing.
  const start = box.dataset.startText ?? "";
  // Start from version 3 served the box with that version's text.
  const startedFrom = box.dataset.startedFrom ?? "";

  const guard = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    // Browsers from before preventDefault was enough.
    event.returnValue = true;
    // Kept by answering Stay: the form that asked to leave is usable again
    // (FR-UI-11), as on the editor.
    window.setTimeout(stayed, 0);
  };
  let guarded = false;
  const guardLeaving = (on: boolean) => {
    if (on === guarded) return;
    guarded = on;
    if (on) window.addEventListener("beforeunload", guard, { signal });
    else window.removeEventListener("beforeunload", guard);
  };

  // Once Save or a confirmed drop is on its way, nothing is kept or asked:
  // the text went with Save, or the answer was to drop it.
  let going = false;
  const unsaved = () => !isVersion(box.value, basedOn, start);
  const note = () => {
    if (going) return;
    const changed = unsaved();
    guardLeaving(changed);
    if (!changed) forget(key);
    else if (startedFrom === "") write(key, { text: box.value, basedOn });
    else write(key, { text: box.value, basedOn, from: startedFrom });
  };

  // Back on the tab, over the version as served: what this browser kept
  // goes back in the box when it started from this version, and is shown
  // to copy when a newer one came since. A box served with other text (a
  // refused save's, which the server kept) is the newer typing. A box
  // Start from filled is served with typing of its own: kept text goes
  // back in it only when it was typed after the same start, and is shown
  // to copy otherwise, so neither is lost.
  const kept = read(key);
  const restore = (text: string) => {
    box.value = text;
    openFold(box);
    const line = document.getElementById(RESTORED);
    if (line) {
      line.hidden = false;
      announceAfterLoad(line.textContent?.trim() ?? "");
    }
  };
  if (kept && kept.text !== box.value) {
    if (startedFrom !== "") {
      if (kept.from === startedFrom) restore(kept.text);
      else write(earlierKey, kept);
    } else if (isVersion(box.value, basedOn, start)) {
      if (kept.basedOn === basedOn) restore(kept.text);
      else write(earlierKey, kept);
    }
  }
  showEarlier(earlierKey, signal);
  note();

  box.addEventListener("input", note, { signal });
  // Back from the back/forward cache: the page is usable again.
  window.addEventListener(
    "pageshow",
    (event) => {
      if (!event.persisted) return;
      going = false;
      note();
    },
    { signal },
  );

  document.addEventListener(
    "submit",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLFormElement)) return;
      if (target === form) {
        // Save takes the text along; a refused one comes back in the box.
        going = true;
        forget(key);
        guardLeaving(false);
        return;
      }
      const question = questionOf(event as SubmitEvent, target);
      if (question === "" || !unsaved()) return;
      if (!window.confirm(question)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      going = true;
      forget(key);
      guardLeaving(false);
    },
    // Capture, so the question comes before anything else acts on it.
    { capture: true, signal },
  );
}

/** Show text kept from an earlier version, to copy, with a way to forget
 * it. */
function showEarlier(earlierKey: string, signal: AbortSignal): void {
  const place = document.getElementById(KEPT_PLACE);
  const field = document.getElementById(KEPT_FIELD);
  const earlier = read(earlierKey);
  if (!place || !(field instanceof HTMLTextAreaElement) || !earlier) return;
  field.value = earlier.text;
  place.hidden = false;
  openFold(place);
  place
    .querySelector<HTMLButtonElement>("[data-forget-plan]")
    ?.addEventListener(
      "click",
      () => {
        forget(earlierKey);
        place.hidden = true;
        // The button is gone with its place: the box takes the focus.
        document.getElementById(BOX)?.focus();
      },
      { signal },
    );
}

/**
 * A link marked `data-open-fold` leads into a fold: the failed drafting
 * notice's way to the instruction box (FR-ARCH-18). It opens the fold
 * the box is in, so the box is in sight when the page scrolls to it, and
 * puts the caret in it. Without the bundle the link still scrolls to the
 * fold.
 */
export function installFoldLinks(): void {
  document.addEventListener("click", (event) => {
    const link = (event.target as Element | null)?.closest?.(
      "a[data-open-fold]",
    );
    const href = link?.getAttribute("href") ?? "";
    if (!href.startsWith("#")) return;
    const target = document.getElementById(href.slice(1));
    if (!target) return;
    openFold(target);
    window.setTimeout(() => target.focus(), 0);
  });
}

/** Where an architecture page says what the write that led to it did. */
const DONE = "[data-arch-done]";

/** Say the outcome of the write that led here, once the page has been read
 * out (FR-ARCH-16, FR-UI-14). */
export function sayDone(): void {
  const done = document.querySelector(DONE);
  if (!done) return;
  announceAfterLoad(spokenText(done.querySelector("p") ?? done));
}

installPlanHooks();
document.addEventListener(AFTER_REFRESH, installPlanHooks);
installFoldLinks();
sayDone();
